import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Search,
    X,
    FileText,
    Loader2,
    Calendar,
    CheckCircle,
    Clock,
    XCircle,
    Archive,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { contratoService, Contrato } from "../../services/contratoService";
import { formatDateFromDB } from "../../utils/dateUtils";

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
    rascunho: { label: "Rascunho", color: "bg-gray-500/20 text-gray-400", icon: Clock },
    ativo: { label: "Ativo", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
    cancelado: { label: "Cancelado", color: "bg-red-500/20 text-red-400", icon: XCircle },
    finalizado: { label: "Finalizado", color: "bg-blue-500/20 text-blue-400", icon: Archive },
};

export function Contratos() {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [contratos, setContratos] = useState<Contrato[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        if (!isAuthenticated) navigate("/area-cliente");
    }, [isAuthenticated, navigate]);

    const clientId = user?.clientId || (user?.role === "client" ? user?.id : undefined);

    useEffect(() => {
        if (clientId) loadContratos();
    }, [clientId, statusFilter, currentPage]);

    const loadContratos = async () => {
        if (!clientId) return;
        setIsLoading(true);
        try {
            const result = await contratoService.list({
                clientId,
                status: statusFilter || undefined,
                page: currentPage,
            });
            setContratos(result.data);
            setTotalPages(result.pagination.totalPages);
        } catch (error) {
            console.error("Erro ao carregar contratos:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatCurrency = (value: number) =>
        new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

    const formatDate = (date?: string | null) =>
        date ? formatDateFromDB(date) : "—";

    const clearFilters = () => { setSearchTerm(""); setStatusFilter(""); setCurrentPage(1); };

    const filteredContratos = contratos.filter((c) =>
        c.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.client?.razaoSocial || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <Helmet>
                <title>Contratos | Portal do Cliente</title>
            </Helmet>

            <div className="min-h-screen pt-20 pb-12">
                <div className="container mx-auto px-4">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <Link
                            to="/portal"
                            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Voltar ao Portal
                        </Link>
                        <h1 className="text-3xl md:text-4xl font-bold">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
                                Contratos
                            </span>
                        </h1>
                        <p className="text-muted-foreground mt-1">Seus contratos vigentes e histórico</p>
                    </motion.div>

                    {/* Filtros */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="flex flex-col md:flex-row gap-4 mb-6 p-4 rounded-xl bg-card border border-border"
                    >
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Buscar contratos..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-10 pr-4 py-2 rounded-lg bg-background border border-input text-foreground placeholder-muted-foreground focus:border-brand focus:ring-1 focus:ring-brand transition-colors"
                            />
                        </div>
                        <div className="flex flex-wrap gap-4 items-center">
                            <select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                className="px-4 py-2 rounded-lg bg-background border border-input text-foreground focus:border-brand focus:ring-1 focus:ring-brand transition-colors"
                            >
                                <option value="">Todos os status</option>
                                <option value="ativo">Ativo</option>
                                <option value="finalizado">Finalizado</option>
                                <option value="cancelado">Cancelado</option>
                            </select>
                            {(searchTerm || statusFilter) && (
                                <button
                                    onClick={clearFilters}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                    Limpar
                                </button>
                            )}
                        </div>
                    </motion.div>

                    {/* Tabela */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="rounded-xl bg-card border border-border overflow-hidden"
                    >
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 text-brand animate-spin" />
                            </div>
                        ) : filteredContratos.length === 0 ? (
                            <div className="text-center py-12">
                                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground">Nenhum contrato encontrado</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-muted/50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Título</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">Período</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Valor/mês</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Vcto dia</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {filteredContratos.map((contrato) => {
                                            const st = statusConfig[contrato.status] ?? statusConfig.rascunho;
                                            const StatusIcon = st.icon;
                                            return (
                                                <tr key={contrato.id} className="hover:bg-muted/50 transition-colors">
                                                    <td className="px-4 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <FileText className="w-5 h-5 text-brand flex-shrink-0" />
                                                            <span className="text-sm text-foreground">{contrato.titulo}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 hidden md:table-cell">
                                                        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                                                            <Calendar className="w-3 h-3" />
                                                            {formatDate(contrato.dataInicio)}
                                                            {" → "}
                                                            {contrato.dataFim ? formatDate(contrato.dataFim) : "vigente"}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 hidden sm:table-cell">
                                                        <span className="text-sm font-medium text-foreground">
                                                            {formatCurrency(Number(contrato.valorMensal))}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 hidden lg:table-cell">
                                                        <span className="text-sm text-muted-foreground">
                                                            Dia {contrato.diaVencimento}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${st.color}`}>
                                                            <StatusIcon className="w-3 h-3" />
                                                            {st.label}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Paginação */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 p-4 border-t border-border">
                                <button
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Anterior
                                </button>
                                <span className="text-sm text-muted-foreground">Página {currentPage} de {totalPages}</span>
                                <button
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Próxima
                                </button>
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>
        </>
    );
}
